import { getCurrentUser, fetchAuthSession } from '@aws-amplify/auth';
import { getAWSConfig } from '../aws.config';

interface TitleUpdateMessage {
  type: 'conversation_title_updated';
  conversationId: string;
  title: string;
  timestamp: number;
}

interface TitleUpdateData {
  conversationId: string;
  title: string;
}

interface ConversationCreatedMessage {
  type: 'conversationCreated';
  conversation: {
    conversationId: string;
    title: string;
    createdAt: string;
    lastActivity: string;
    attachmentCount: number;
  };
  timestamp: string;
}

interface ConversationCreatedData {
  conversation: {
    conversationId: string;
    title: string;
    createdAt: string;
    lastActivity: string;
    attachmentCount: number;
  };
}

// Simple event emitter for handling title updates
class SimpleEventEmitter {
  private listeners: Map<string, Set<(data: unknown) => void>> = new Map();

  on(event: string, callback: (data: unknown) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: (data: unknown) => void): void {
    this.listeners.get(event)?.delete(callback);
  }

  emit(event: string, data: unknown): void {
    this.listeners.get(event)?.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in event callback:', error);
      }
    });
  }
}

interface TokenPayload {
  aud: string;
  token_use: string;
  iss: string;
  client_id: string;
  exp: number;
}

function isTokenPayload(obj: unknown): obj is TokenPayload {
  return typeof obj === 'object' && obj !== null &&
    'aud' in obj && typeof (obj as TokenPayload).aud === 'string' &&
    'token_use' in obj && typeof (obj as TokenPayload).token_use === 'string' &&
    'iss' in obj && typeof (obj as TokenPayload).iss === 'string' &&
    'client_id' in obj && typeof (obj as TokenPayload).client_id === 'string' &&
    'exp' in obj && typeof (obj as TokenPayload).exp === 'number';
}

interface WebSocketMessageData {
  type: string;
  [key: string]: unknown;
}

function isWebSocketMessageData(obj: unknown): obj is WebSocketMessageData {
  return typeof obj === 'object' && obj !== null && 'type' in obj;
}

/**
 * WebSocket connection manager for real-time chat
 */
/** Partner portal a conversation is embedded in, carried through reconnections. */
export interface PartnerContext {
  partner: 'laboklin';
  labReportNo?: string;
  labMode?: 'pre_order' | 'post_result';
}

export interface ConnectOptions {
  insuranceReportId?: string;
  partner?: 'laboklin';
  labReportNo?: string;
  labMode?: 'pre_order' | 'post_result';
}

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private currentWsInstance: WebSocket | null = null; // Track current instance to prevent old handlers from interfering
  private messageHandlers: Set<(data: unknown) => void> = new Set();
  private connectionHandlers: Set<(connected: boolean) => void> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private pingInterval: NodeJS.Timeout | null = null;
  private eventEmitter = new SimpleEventEmitter();
  private currentConversationId?: string; // Store current conversation ID for reconnections
  private currentInsuranceReportId?: string; // Store insurance report ID for reconnections
  private currentPartnerContext?: PartnerContext; // Partner portal context for reconnections

  constructor(private wsEndpoint: string) {}

  /**
   * Connect to WebSocket with authentication and conversationId
   */
  async connect(conversationId?: string, options?: ConnectOptions): Promise<void> {
    // Store conversation ID and linked context for reconnections
    if (conversationId) {
      this.currentConversationId = conversationId;
    }
    if (options?.insuranceReportId) {
      this.currentInsuranceReportId = options.insuranceReportId;
    }
    if (options?.partner) {
      this.currentPartnerContext = {
        partner: options.partner,
        labReportNo: options.labReportNo,
        labMode: options.labMode,
      };
    }

    // If already connected or connecting to the SAME conversation, don't create a new connection
    if (this.ws && (this.ws.readyState === 1 || this.ws.readyState === 0) && this.currentConversationId === conversationId) { // OPEN or CONNECTING
      console.log('WebSocket already connected or connecting to the same conversation');
      return;
    }

    // Clean up any existing connection before creating a new one
    if (this.ws) {
      console.log('Cleaning up existing WebSocket connection before connecting to new conversation');
      // Close the WebSocket before cleanup to prevent orphaned connections
      this.ws.close(1000, 'Switching conversations');
      this.cleanup();
    }

    try {
      // Get current user session to extract JWT token
      await getCurrentUser(); // Verify user is authenticated
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();

      if (!token) {
        throw new Error('No authentication token available');
      }

      // Log token info for debugging (decode without verification)
      try {
        const tokenParts = token.split('.');
        const payload: unknown = JSON.parse(atob(tokenParts[1]));
        if (isTokenPayload(payload)) {
          console.log('Frontend: Sending token with payload:', {
            aud: payload.aud,
            token_use: payload.token_use,
            iss: payload.iss,
            client_id: payload.client_id,
            exp: new Date(payload.exp * 1000).toISOString(),
          });
        }
      } catch (e) {
        console.warn('Could not decode token for logging:', e);
      }

      // Build WebSocket URL with authentication token and conversationId
      const queryParams = new URLSearchParams({
        token: token,
      });
      
      if (conversationId) {
        queryParams.append('conversationId', conversationId);
      }
      
      // Get role from localStorage (if set)
      const role = localStorage.getItem('gekkovet-chat-role') as 'veterinary_professional' | 'pet_owner' | null;
      if (role && (role === 'veterinary_professional' || role === 'pet_owner')) {
        queryParams.append('role', role);
        console.log('Connecting to WebSocket with role:', role);
      }

      // Pass insurance report ID if linked
      if (this.currentInsuranceReportId) {
        queryParams.append('insuranceReportId', this.currentInsuranceReportId);
        console.log('Connecting to WebSocket with insuranceReportId:', this.currentInsuranceReportId);
      }

      // Pass partner portal context if this conversation is embedded in one
      if (this.currentPartnerContext?.partner) {
        queryParams.append('partner', this.currentPartnerContext.partner);
        if (this.currentPartnerContext.labReportNo) {
          queryParams.append('labReportNo', this.currentPartnerContext.labReportNo);
        }
        if (this.currentPartnerContext.labMode) {
          queryParams.append('labMode', this.currentPartnerContext.labMode);
        }
        console.log('Connecting to WebSocket with partner context:', this.currentPartnerContext);
      }
      
      const wsUrl = `${this.wsEndpoint}?${queryParams.toString()}`;
      
      console.log('Connecting to WebSocket with conversationId:', conversationId);
      this.ws = new WebSocket(wsUrl);
      this.currentWsInstance = this.ws; // Track this instance

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.notifyConnectionHandlers(true);
        this.startPing();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const data: unknown = JSON.parse(event.data as string);
          console.log('WebSocket message received:', data);
          
          if (isWebSocketMessageData(data)) {
            // Handle title update messages
            if (data.type === 'conversation_title_updated') {
              this.handleTitleUpdate(data as unknown as TitleUpdateMessage);
            }
            
            // Handle conversation created messages
            if (data.type === 'conversationCreated') {
              this.handleConversationCreated(data as unknown as ConversationCreatedMessage);
            }
            
            this.notifyMessageHandlers(data);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = (event: CloseEvent) => {
        console.log('WebSocket closed:', event.code, event.reason);
        
        // Only cleanup if this close event is from the current WebSocket instance
        // We check if the event target matches the current stored instance
        const eventTarget = (event as CloseEvent & { target: WebSocket }).target;
        if (eventTarget === this.currentWsInstance) {
          console.log('Cleaning up current WebSocket instance');
          this.cleanup();
          this.notifyConnectionHandlers(false);
          
          // Attempt to reconnect if not intentionally closed
          const eventCode = (event as CloseEvent).code;
          if (eventCode !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect(this.currentConversationId);
          }
        } else {
          console.log('Ignoring close event from old WebSocket instance');
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      throw error;
    }
  }

  /**
   * Send a message through the WebSocket
   */
  sendMessage(message: string, conversationId: string, attachmentIds?: string[]): void {
    // Check WebSocket state more thoroughly
    if (!this.ws) {
      throw new Error('WebSocket is not initialized');
    }
    
    if (this.ws.readyState !== 1) { // 1 = WebSocket.OPEN
      throw new Error(`WebSocket is not connected (readyState: ${this.ws.readyState})`);
    }

    const payload = {
      action: 'sendMessage',
      message,
      conversationId,
      attachmentIds: attachmentIds || [],
      timestamp: new Date().toISOString(),
    };

    try {
      (this.ws as WebSocket).send(JSON.stringify(payload));
      console.log('Message sent:', payload);
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
      throw new Error('Failed to send message through WebSocket');
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    console.log('Disconnecting WebSocket...');
    if (this.ws) {
      this.ws.close(1000, 'User initiated disconnect');
    }
    this.cleanup();
    // Notify handlers that connection is now closed
    this.notifyConnectionHandlers(false);
    // Clear conversation ID and insurance report ID on intentional disconnect
    this.currentConversationId = undefined;
    this.currentInsuranceReportId = undefined;
    this.currentPartnerContext = undefined;
  }

  /**
   * Add message handler
   */
  onMessage(handler: (data: any) => void): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  /**
   * Add connection status handler
   */
  onConnectionChange(handler: (connected: boolean) => void): () => void {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  /**
   * Get current connection status
   */
  isConnected(): boolean {
    if (!this.ws) {
      console.log('isConnected: false - no WebSocket instance');
      return false;
    }
    const ws = this.ws as WebSocket;
    const readyState = ws.readyState;
    const connected = readyState === 1; // 1 = WebSocket.OPEN
    console.log(`isConnected: ${connected} - readyState: ${readyState} (0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED)`);
    return connected;
  }

  /**
   * Get the current conversation ID this WebSocket is connected to
   */
  getCurrentConversationId(): string | undefined {
    return this.currentConversationId;
  }

  /**
   * Subscribe to title updates
   */
  onTitleUpdate(callback: (data: TitleUpdateData) => void): () => void {
    const wrappedCallback = (data: unknown) => {
      callback(data as TitleUpdateData);
    };
    this.eventEmitter.on('titleUpdated', wrappedCallback);
    
    // Return unsubscribe function
    return () => this.eventEmitter.off('titleUpdated', wrappedCallback);
  }

  /**
   * Subscribe to conversation created events
   */
  onConversationCreated(callback: (data: ConversationCreatedData) => void): () => void {
    const wrappedCallback = (data: unknown) => {
      callback(data as ConversationCreatedData);
    };
    this.eventEmitter.on('conversationCreated', wrappedCallback);
    
    // Return unsubscribe function
    return () => this.eventEmitter.off('conversationCreated', wrappedCallback);
  }

  /**
   * Handle title update messages
   */
  private handleTitleUpdate(message: TitleUpdateMessage): void {
    console.log('Title update received:', message);
    console.log('Emitting titleUpdated event for conversation:', message.conversationId, 'with title:', message.title);
    
    // Emit event for components to listen to
    this.eventEmitter.emit('titleUpdated', {
      conversationId: message.conversationId,
      title: message.title
    });
    
    console.log('titleUpdated event emitted successfully');
  }

  /**
   * Handle conversation created messages
   */
  private handleConversationCreated(message: ConversationCreatedMessage): void {
    console.log('Conversation created event received:', message);
    
    // Emit event for components to listen to
    this.eventEmitter.emit('conversationCreated', {
      conversation: message.conversation
    });
  }

  private cleanup(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    this.ws = null;
    this.currentWsInstance = null;
  }

  private notifyMessageHandlers(data: any): void {
    this.messageHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error('Error in message handler:', error);
      }
    });
  }

  private notifyConnectionHandlers(connected: boolean): void {
    this.connectionHandlers.forEach(handler => {
      try {
        handler(connected);
      } catch (error) {
        console.error('Error in connection handler:', error);
      }
    });
  }

  private scheduleReconnect(conversationId?: string): void {
    this.reconnectAttempts++;
    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${this.reconnectDelay}ms`);
    
    setTimeout(() => {
      this.connect(conversationId).catch(error => {
        console.error('Reconnect failed:', error);
        
        // Exponential backoff
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000); // Max 30 seconds
        
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect(conversationId);
        } else {
          console.error('Max reconnect attempts reached');
        }
      });
    }, this.reconnectDelay);
  }

  private startPing(): void {
    // Send ping every 30 seconds to keep connection alive
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ action: 'ping' }));
      }
    }, 30000);
  }
}

// Singleton instance
let wsManager: WebSocketManager | null = null;

/**
 * Get or create WebSocket manager instance
 */
export const getWebSocketManager = (): WebSocketManager => {
  if (!wsManager) {
    // Get WebSocket endpoint from environment variables
    const config = getAWSConfig();
    const wsEndpoint = config.wsEndpoint || process.env.WS_ENDPOINT || 'wss://your-api-endpoint.amazonaws.com/dev';
    wsManager = new WebSocketManager(wsEndpoint);
  }
  return wsManager;
};
